import { ArrowRight, Layers } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import type { SchemaPattern } from '@/features/schema/types'
import { DetailCardHeading } from './DetailCardHeading'

export interface PatternDetailCardProps {
  pattern: SchemaPattern
  onEdit: (pattern: SchemaPattern) => void
  onDelete: (patternId: string) => void
  /** Hide the edit/delete actions when viewing a read-only (non-latest) version. */
  readOnly?: boolean
}

/** Right-panel detail view for a selected extraction pattern. */
export function PatternDetailCard({
  pattern,
  onEdit,
  onDelete,
  readOnly = false,
}: PatternDetailCardProps) {
  const { t } = useTranslation()
  return (
    <Card className="bg-card/50 border-border flex-1">
      <CardHeader className="pb-3">
        <DetailCardHeading
          key={pattern.id}
          icon={<Layers className="text-success-foreground" />}
          title={t('schemaAgent.editor.pattern')}
          actions={
            readOnly
              ? undefined
              : { onEdit: () => onEdit(pattern), onDelete: () => onDelete(pattern.id) }
          }
        />
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pattern Visualization. Below md it stacks top to bottom (arrow turned
            down) so three long type names never run out of the card. */}
        <div className="flex max-md:flex-col items-center justify-center gap-3 p-4 bg-muted/30 rounded-lg">
          <div className="max-md:max-w-full text-center">
            <Badge className="max-md:max-w-full bg-info-surface text-info-foreground border-info-border text-sm px-3 py-1">
              {pattern.source_type}
            </Badge>
            <p className="text-xs text-muted-foreground mt-1">{t('schemaAgent.editor.source')}</p>
          </div>
          <div className="flex max-md:max-w-full flex-col items-center">
            <ArrowRight className="h-5 w-5 max-md:rotate-90 text-warning-foreground" />
            <Badge
              variant="outline"
              className="max-md:max-w-full bg-warning-surface text-warning-foreground border-warning-border font-mono text-xs mt-1"
            >
              {pattern.relationship_type}
            </Badge>
          </div>
          <div className="max-md:max-w-full text-center">
            <Badge className="max-md:max-w-full bg-info-surface text-info-foreground border-info-border text-sm px-3 py-1">
              {pattern.target_type}
            </Badge>
            <p className="text-xs text-muted-foreground mt-1">{t('schemaAgent.editor.target')}</p>
          </div>
        </div>

        {/* Description */}
        {pattern.description && (
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
              {t('schemaAgent.editor.description')}
            </div>
            <p className="text-sm text-foreground">{pattern.description}</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
